import React, { useEffect, useState } from 'react';
import { ModalShell } from '../ds/ModalShell';
import { Button } from '../ds/components/Button';
import { Icon } from '../ds/components/Icon';
import {
  buildingsApi,
  HeatSourceTotalsBulkResponse,
  HeatSourceTotalsSelection,
} from '../services/api';
import {
  countOfType,
  heatSourceColumnLabel,
  heatSourceSummaryColumns,
  heatSourceSummaryCsv,
} from '../utils/heatSourceSummary';
import { asciiFilename, saveBlob } from '../utils/download';
import { budynkiLabel } from '../utils/plural';

export interface HeatSourceSummaryModalProps {
  /** Co podsumować; `null` = modal zamknięty. */
  selection: HeatSourceTotalsSelection | null;
  /** Dopisek do tytułu (np. nazwa zarządcy) i do nazwy pliku CSV. */
  subject?: string;
  onClose: () => void;
}

const th: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  textAlign: 'right',
  fontWeight: 'var(--weight-semibold)' as any,
  color: 'var(--text-heading)',
  borderBottom: 'var(--border-width-strong) solid var(--border-strong)',
  whiteSpace: 'nowrap',
  position: 'sticky',
  top: 0,
  background: 'var(--surface-panel)',
};
const td: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-3)',
  textAlign: 'right',
  color: 'var(--text-primary)',
  borderBottom: 'var(--border-width) solid var(--border)',
  fontVariantNumeric: 'tabular-nums',
};

/**
 * Zestawienie INDYWIDUALNYCH źródeł ciepła dla wielu budynków (właściciel: „podsumowanie per
 * budynek 1, 2, 3, 4"; kontrakt #326): wiersze = budynki, kolumny = typy źródeł, ostatni wiersz =
 * suma. Reguła liczenia = sekcja „Źródła ciepła w lokalach" w szczegółach budynku (ten sam loader
 * w backendzie). Eksport: CSV (Excel, separator `;`).
 */
export default function HeatSourceSummaryModal({ selection, subject, onClose }: HeatSourceSummaryModalProps) {
  const [data, setData] = useState<HeatSourceTotalsBulkResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Klucz zamiast obiektu: rodzic podaje nowy literał co render, a pobieramy tylko przy zmianie wyboru.
  const key = selection ? JSON.stringify(selection) : '';
  useEffect(() => {
    if (!key) { setData(null); setError(null); return undefined; }
    let stale = false; // guard anty-stale: zamknięcie/zmiana wyboru w trakcie nie nadpisze wyniku
    setLoading(true);
    setData(null);
    setError(null);
    buildingsApi.heatSourceTotals(JSON.parse(key))
      .then((res) => { if (!stale) setData(res); })
      .catch((e) => { if (!stale) setError(e instanceof Error ? e.message : 'Nie udało się policzyć źródeł ciepła'); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [key]);

  const cols = data ? heatSourceSummaryColumns(data) : [];
  const exportCsv = () => {
    if (!data) return;
    const blob = new Blob([heatSourceSummaryCsv(data)], { type: 'text/csv;charset=utf-8' });
    const day = new Date().toISOString().slice(0, 10);
    saveBlob(blob, `zrodla_ciepla_${asciiFilename(subject || 'budynki')}_${day}.csv`);
  };

  const row = (label: React.ReactNode, t: HeatSourceTotalsBulkResponse['totals'], bold = false, key?: string) => (
    <tr key={key}>
      <td style={{ ...td, textAlign: 'left', fontWeight: bold ? ('var(--weight-bold)' as any) : undefined, color: 'var(--text-heading)' }}>
        {label}
      </td>
      {cols.map((c) => {
        const n = countOfType(t, c);
        return (
          <td key={c} style={{ ...td, fontWeight: bold ? ('var(--weight-bold)' as any) : undefined, color: n ? td.color : 'var(--text-muted)' }}>
            {n || '–'}
          </td>
        );
      })}
      <td style={{ ...td, fontWeight: 'var(--weight-bold)' as any }}>{t.total}</td>
      <td style={{ ...td, color: t.apartmentsUnknown ? 'var(--status-deny)' : 'var(--text-muted)' }}>
        {t.apartmentsUnknown || '–'}
      </td>
    </tr>
  );

  return (
    <ModalShell
      open={!!selection}
      title={subject ? `Podsumowanie źródeł ciepła — ${subject}` : 'Podsumowanie źródeł ciepła'}
      width={1040}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Zamknij</Button>
          <Button variant="primary" icon="download" onClick={exportCsv} disabled={!data || data.buildings.length === 0}>
            Pobierz CSV (Excel)
          </Button>
        </>
      }
    >
      <div data-testid="heat-source-summary" style={{ display: 'grid', gap: 'var(--space-4)', fontFamily: 'var(--font-sans)' }}>
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>
          Indywidualne źródła ciepła w lokalach (sztuki) — zapis kominiarza z bieżącego cyklu, dla pozostałych
          lokali dane z CEEB. Wspólne źródła budynków nie wchodzą do zestawienia.
        </p>
        {loading && <div style={{ color: 'var(--text-secondary)' }}>Liczenie…</div>}
        {error && (
          <div
            role="alert"
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
              padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)',
              background: 'var(--status-deny-bg)', border: 'var(--border-width) solid var(--status-deny-bar)',
              color: 'var(--status-deny)', fontSize: 'var(--text-label)',
            }}
          >
            <Icon name="triangle-alert" size={18} />
            <span>{error}</span>
          </div>
        )}
        {data && data.skipped.length > 0 && (
          <div role="status" style={{ color: 'var(--status-rec)', fontSize: 'var(--text-label)' }}>
            Pominięto {budynkiLabel(data.skipped.length)} (brak dostępu lub budynek nie istnieje).
          </div>
        )}
        {data && data.buildings.length === 0 && (
          <div style={{ color: 'var(--text-secondary)' }}>Brak budynków do podsumowania.</div>
        )}
        {data && data.buildings.length > 0 && (
          <div style={{ overflow: 'auto', maxHeight: '60vh' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 'var(--text-label)' }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }}>Budynek</th>
                  {cols.map((c) => <th key={c} style={th}>{heatSourceColumnLabel(c)}</th>)}
                  <th style={th}>Razem</th>
                  <th style={th} title="Lokale bez zapisu kominiarza i bez źródeł w CEEB — ich źródła nie są policzone">
                    Lokale bez danych
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.buildings.map((b) => row(b.address, b.totals, false, b.buildingId))}
                {row(`Suma (${budynkiLabel(data.buildings.length)})`, data.totals, true, '__sum')}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ModalShell>
  );
}
